import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

const useStyles = makeStyles({
  root: {
    padding: "2rem 1rem",
    backgroundColor: "#EBF5F3",
    borderRadius: "12px",
  },
  textField: {
    backgroundColor: "white",
    border: "1px solid black",
  },
  textFieldDisabled: {
    backgroundColor: "white",
  },
});

const TSP_ONBOARDING_STATUS = [
  {
    value: "NOT_STARTED",
    label: "Not Started",
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
  },
  {
    value: "APP_REVIEW_COMPLETED",
    label: "App Review Completed",
  },
  {
    value: "SOLUTION_PARTNER_APPROVED",
    label: "Solution Partner Approved",
  },
  {
    value: "TSP_COMPLETED",
    label: "TSP Completed",
  },
  {
    value: "TECH_PARTNER",
    label: "Tech Partner",
  },
];
/**
 * @typedef {import("../../types").Partner} Partner
 */

/**
 * @typedef {Object} PartnerEmbeddedInputProps
 * @property {Partner} partner
 * @property {Agent} agent
 * @property {(key: string, value: string) => Promise<void>} updateAttribute
 */

/**
 * @param {PartnerEmbeddedInputProps} props
 */
function PartnerEmbeddedInput({
  partner,
  agent,
  updateAttribute,
  hasWriteAccess,
}) {
  const classes = useStyles();
  const [
    {
      appId,
      appSecret,
      solutionId,
      businessManagerId,
      systemUserAccessToken,
      esConfigId,
    },
    setState,
  ] = useState({
    appId: partner?.appId || "",
    appSecret: partner?.appSecret || "",
    solutionId: partner?.solutionId || "",
    businessManagerId: partner?.businessManagerId || "",
    systemUserAccessToken: partner?.systemUserAccessToken || "",
    esConfigId: partner?.esConfigId || "",
  });

  const [selectedTspOnboardingStatus, setSelectedTspOnboardingStatus] =
    useState(partner?.tspOnboardingStatus?.status ?? "NOT_STARTED");
  const [tspOnboardingStatusRemark, setTspOnboardingStatusRemark] = useState(
    partner?.tspOnboardingStatus?.remark ?? ""
  );

  const setAttribute = (key, value) => {
    setState((state) => ({ ...state, [key]: value }));
  };

  const handleAttriuteUpdate = async (key, value) => {
    await updateAttribute(key, value);
  };

  return (
    <fieldset
      disabled={!hasWriteAccess}
      style={{ border: "none", padding: 0, margin: 0 }}
    >
      <Box className={classes.root}>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Change TSP Onboarding Status
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current TSP Onboarding Status -{" "}
          {partner?.tspOnboardingStatus?.status || "Nill"}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Autocomplete
              disableClearable
              options={TSP_ONBOARDING_STATUS}
              getOptionLabel={(option) => option.label}
              onChange={(e, value) =>
                setSelectedTspOnboardingStatus(value?.value ?? "")
              }
              value={
                TSP_ONBOARDING_STATUS.find(
                  (option) => option.value === selectedTspOnboardingStatus
                ) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.textField}
                  InputProps={{ ...params.InputProps }}
                  placeholder="Select partnership status"
                  style={{
                    border: "1px solid ",
                    background: "white",
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="tspOnboardingStatusRemark"
              value={tspOnboardingStatusRemark}
              onChange={(e) => setTspOnboardingStatusRemark(e.target.value)}
              placeholder="Enter Remark (Required)"
              className={classes.textField}
              fullWidth
              variant="outlined"
              size="small"
              style={{ padding: "0px" }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={async () => {
                await updateAttribute("tspOnboardingStatus", {
                  status: selectedTspOnboardingStatus,
                  remark: tspOnboardingStatusRemark,
                });
              }}
              disabled={
                !selectedTspOnboardingStatus ||
                !tspOnboardingStatusRemark.trim() ||
                (selectedTspOnboardingStatus ===
                  (partner?.tspOnboardingStatus?.status ?? "") &&
                  tspOnboardingStatusRemark ===
                    (partner?.tspOnboardingStatus?.remark ?? ""))
              }
            >
              Update Status
            </Button>
          </Grid>
        </Grid>
        <Box my={5}>{/* <Divider /> */}</Box>

        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner App ID - Enter the App ID to update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current Partner App ID - {partner.appId}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={appId}
              onChange={(e) => setAttribute("appId", e.target.value)}
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAttriuteUpdate("appId", appId)}
            >
              Update App ID
            </Button>
          </Grid>
        </Grid>

        {/** Partner App Secret */}
        <Box my={6}>{/* <Divider /> */}</Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner App Secret - Enter the App Secret to update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current App Secret - {partner.appSecret}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={appSecret}
              onChange={(e) => setAttribute("appSecret", e.target.value)}
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAttriuteUpdate("appSecret", appSecret)}
            >
              Update App Secret
            </Button>
          </Grid>
        </Grid>

        {/** Partner ES Config ID */}
        <Box my={6}>{/* <Divider /> */}</Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner ES Config ID - Enter the ES Config ID to update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current ES Config ID- {partner.esConfigId}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={esConfigId}
              onChange={(e) => setAttribute("esConfigId", e.target.value)}
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAttriuteUpdate("esConfigId", esConfigId)}
            >
              Update ES Config ID
            </Button>
          </Grid>
        </Grid>

        {/** Partner Solution ID */}
        <Box my={6}>{/* <Divider /> */}</Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner Solution ID - Enter the Solution ID to update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current Partner Solution ID - {partner.solutionId}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={solutionId}
              onChange={(e) => setAttribute("solutionId", e.target.value)}
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAttriuteUpdate("solutionId", solutionId)}
            >
              Update Solution ID
            </Button>
          </Grid>
        </Grid>

        {/** Partner FB Business Manager ID */}
        <Box my={6}>{/* <Divider /> */}</Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner FB Business Manger ID - Enter the FB Business Manager ID to
          update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current Partner FB Business Manager ID - {partner.businessManagerId}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={businessManagerId}
              onChange={(e) =>
                setAttribute("businessManagerId", e.target.value)
              }
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                handleAttriuteUpdate("businessManagerId", businessManagerId)
              }
            >
              Update FB Business Manager ID
            </Button>
          </Grid>
        </Grid>
        {/** Partner System User Token ID */}
        <Box my={6}>{/* <Divider /> */}</Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner System User Token - Enter the System User Token to update
        </Typography>
        <Typography variant="body1" gutterBottom>
          Current Partner System User Token -{" "}
          {partner.systemUserAccessToken ? "SET" : "NOT SET"}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              value={
                systemUserAccessToken === true
                  ? "********************"
                  : systemUserAccessToken
              }
              onChange={(e) =>
                setAttribute("systemUserAccessToken", e.target.value)
              }
              className={classes.textField}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                handleAttriuteUpdate(
                  "systemUserAccessToken",
                  systemUserAccessToken
                )
              }
            >
              Update Token
            </Button>
          </Grid>
        </Grid>
      </Box>
    </fieldset>
  );
}
const connectedPartnerEmbeddedInput = connect((state) => ({
  agent: state.login.user,
}))(PartnerEmbeddedInput);

export default connectedPartnerEmbeddedInput;
